import { createFileRoute } from '@tanstack/react-router'
import { FileText, Plus, Check, DollarSign } from 'lucide-react'
import { useState } from 'react'

import { useCurrencyFormatter } from '@/hooks'
import {
  usePayslips,
  useGeneratePayslips,
  useApprovePayslip,
  useMarkPayslipAsPaid,
  useBatchApprovePayslips,
  useBatchMarkPayslipsAsPaid,
  type PayslipStatus,
  type PayslipListParams,
  type PayslipWithEmployee,
} from '@/hooks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/hr/salaries/payslips')({
  component: PayslipListPage,
})

const STATUS_LABELS: Record<PayslipStatus, string> = {
  draft: '草稿',
  pending: '待审批',
  approved: '已审批',
  paid: '已发放',
}

const STATUS_COLORS: Record<PayslipStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
}



function PayslipListPage() {
  const [filters, setFilters] = useState<PayslipListParams>({ page: 1, pageSize: 20 })
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [generateMonth, setGenerateMonth] = useState('')
  const [showGenerateForm, setShowGenerateForm] = useState(false)

  const { data, isLoading, error } = usePayslips(filters)
  const generateMutation = useGeneratePayslips()
  const approveMutation = useApprovePayslip()
  const payMutation = useMarkPayslipAsPaid()
  const batchApproveMutation = useBatchApprovePayslips()
  const batchPayMutation = useBatchMarkPayslipsAsPaid()
  const { formatMoney } = useCurrencyFormatter()

  const handleStatusFilter = (status: PayslipStatus | undefined) => {
    setFilters((prev) => ({ ...prev, status, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  const handleGenerate = () => {
    if (!generateMonth) { return }
    generateMutation.mutate(generateMonth, {
      onSuccess: () => {
        setShowGenerateForm(false)
        setGenerateMonth('')
      },
    })
  }

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) }
      else { next.add(id) }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === payslips.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(payslips.map((p) => p.id)))
    }
  }

  const handleBatchApprove = () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) { return }
    batchApproveMutation.mutate(ids, { onSuccess: () => setSelectedIds(new Set()) })
  }

  const handleBatchPay = () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) { return }
    batchPayMutation.mutate(ids, { onSuccess: () => setSelectedIds(new Set()) })
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  const payslips = data?.data ?? []
  const meta = data?.meta

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">薪资单管理</h1>
          <p className="text-muted-foreground">生成和管理月度薪资单</p>
        </div>
        <button
          onClick={() => setShowGenerateForm(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          生成薪资单
        </button>
      </div>

      {/* 生成薪资单表单 */}
      {showGenerateForm && (
        <div className="rounded-lg border p-4">
          <h3 className="mb-4 font-semibold">生成月度薪资单</h3>
          <div className="flex items-end gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">月份</label>
              <input
                type="month"
                value={generateMonth}
                onChange={(e) => setGenerateMonth(e.target.value)}
                className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <button
              onClick={handleGenerate}
              disabled={!generateMonth || generateMutation.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {generateMutation.isPending ? '生成中...' : '生成'}
            </button>
            <button
              onClick={() => setShowGenerateForm(false)}
              className="rounded-md px-4 py-2 text-sm hover:bg-muted"
            >
              取消
            </button>
          </div>
          {generateMutation.error && (
            <p className="mt-2 text-sm text-destructive">{generateMutation.error.message}</p>
          )}
        </div>
      )}

      {/* 筛选和批量操作 */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleStatusFilter(undefined)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              !filters.status ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            全部
          </button>
          {(Object.keys(STATUS_LABELS) as PayslipStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                filters.status === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              {STATUS_LABELS[status]}
            </button>
          ))}
        </div>

        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">已选 {selectedIds.size} 项</span>
            <button
              onClick={handleBatchApprove}
              disabled={batchApproveMutation.isPending}
              className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Check className="h-4 w-4" />
              批量审批
            </button>
            <button
              onClick={handleBatchPay}
              disabled={batchPayMutation.isPending}
              className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              <DollarSign className="h-4 w-4" />
              批量发放
            </button>
          </div>
        )}
      </div>

      {/* 列表 */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : payslips.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <FileText className="h-12 w-12 text-muted-foreground" />
          <div className="text-center">
            <p className="text-lg font-medium">暂无薪资单</p>
            <p className="text-sm text-muted-foreground">点击上方按钮生成月度薪资单</p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === payslips.length && payslips.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">员工</th>
                <th className="px-4 py-3 text-left text-sm font-medium">月份</th>
                <th className="px-4 py-3 text-left text-sm font-medium">基本工资</th>
                <th className="px-4 py-3 text-left text-sm font-medium">津贴</th>
                <th className="px-4 py-3 text-left text-sm font-medium">扣款</th>
                <th className="px-4 py-3 text-left text-sm font-medium">实发</th>
                <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                <th className="px-4 py-3 text-left text-sm font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {payslips.map((payslip: PayslipWithEmployee) => (
                <tr key={payslip.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(payslip.id)}
                      onChange={() => toggleSelect(payslip.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium">{payslip.employee?.name || '-'}</p>
                      <p className="text-sm text-muted-foreground">{payslip.employee?.employeeNo}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{payslip.month}</td>
                  <td className="px-4 py-3 text-sm">{formatMoney(payslip.baseSalary)}</td>
                  <td className="px-4 py-3 text-sm">{payslip.allowance ? formatMoney(payslip.allowance) : '-'}</td>
                  <td className="px-4 py-3 text-sm text-destructive">
                    {payslip.deduction ? `-${formatMoney(payslip.deduction)}` : '-'}
                  </td>
                  <td className="px-4 py-3 font-medium">{formatMoney(payslip.netSalary)}</td>
                  <td className="px-4 py-3">
                    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', STATUS_COLORS[payslip.status])}>
                      {STATUS_LABELS[payslip.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {(payslip.status === 'draft' || payslip.status === 'pending') && (
                        <button
                          onClick={() => approveMutation.mutate(payslip.id)}
                          disabled={approveMutation.isPending}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          审批
                        </button>
                      )}
                      {payslip.status === 'approved' && (
                        <button
                          onClick={() => payMutation.mutate(payslip.id)}
                          disabled={payMutation.isPending}
                          className="text-sm text-green-600 hover:underline"
                        >
                          发放
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 分页 */}
      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            共 {meta.total} 条记录，第 {meta.page}/{meta.totalPages} 页
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(meta.page - 1)}
              disabled={meta.page <= 1}
              className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              上一页
            </button>
            <button
              onClick={() => handlePageChange(meta.page + 1)}
              disabled={meta.page >= meta.totalPages}
              className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              下一页
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
